import React, { useCallback, useEffect, useState } from 'react'
import { Table, Thead, Tbody, Tr, Th, Td, Text, Flex, Button, Tfoot } from '@chakra-ui/react'
import { EventSummary } from '../../services/network'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { createEventApi } from '../../utils/api-interface'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { log } from '../../utils/logger'
import axios from 'axios'
import { RetryDialog } from '../RetryDialog/RetryDialog'
import { formatISODate } from '../../utils/utils'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import dayjs from 'dayjs'
import { setCurrentPage } from '../../store/slices/eventSearchSlice'
import { getEventSearchFilters, getEventStatusLabelKey } from '../../utils/event-status'

interface IPaginatedEventGeneralResponse {
  currentPage: number
  totalPages: number
  totalItems: number
  events: Array<EventSummary>
}

function SectionEventTable(): JSX.Element {
  const [eventResults, setEventResults] = useState<IPaginatedEventGeneralResponse>()

  const { t } = useTranslation()
  const [errorData, setErrorData] = useState<any>(null)
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)
  const handleCloseRetryDialog = (): void => setIsRetryDialogOpen(false)
  const navigate = useNavigate()

  const searchEvent = useSelector((state: RootState) => state.searchEvent)
  const dispatch = useDispatch()

  const handleSearch = useCallback((): void => {
    const eventsApiClient = createEventApi()
    const filters = getEventSearchFilters(searchEvent.selectedStatuses)
    eventsApiClient
      .searchEvents(
        searchEvent.token ?? undefined,
        searchEvent.selectedDates[0] ? dayjs(searchEvent.selectedDates[0]).format(`YYYY-MM-DD`) : undefined,
        searchEvent.selectedDates[1] ? dayjs(searchEvent.selectedDates[1]).format(`YYYY-MM-DD`) : undefined,
        filters.status,
        searchEvent.currentPage,
        searchEvent.pageSize,
        filters.noShowCheckIns
      )
      .then((response) => {
        setEventResults(response.data as unknown as IPaginatedEventGeneralResponse)
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          if (error.response) {
            log.error('Error fetching events partecipated:', error.response.data)
            setErrorData(error.response.data)
          } else {
            log.error('No response from the server:', error.message)
          }
        } else {
          log.error('Unexpected error:', error)
        }
        setIsRetryDialogOpen(true)
      })
  }, [searchEvent.currentPage, searchEvent.pageSize, searchEvent.selectedDates, searchEvent.selectedStatuses, searchEvent.token])

  const handleConfirmRetryDialog = (): void => {
    handleSearch()
  }

  const handlePageChange = (page: number): void => {
    if (eventResults) {
      if (page > 0 && page <= eventResults?.totalPages) {
        dispatch(setCurrentPage(page))
      }
    }
  }

  const goToEvent = useCallback(
    (id: string) => {
      navigate(`/events/${id}`)
    },
    [navigate]
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => handleSearch(), 400)
    return () => clearTimeout(timeoutId)
  }, [searchEvent, handleSearch])

  useEffect(() => {
    eva.replace()
  })

  return (
    <div>
      <Table bg={'white'}>
        <Thead height={'40px'}>
          <Th width='121px'>{t('unique_code')}</Th>
          <Th width={'512px'}>{t('event_name')}</Th>
          <Th width={'120px'}>{t('event_date')}</Th>
          <Th width={'90px'}>{t('status')}</Th>
          <Th width={'90px'}>{t('partecipant_number')}</Th>
          <Th width={'40px'}></Th>
        </Thead>
        <Tbody>
          {eventResults && eventResults.events ? (
            eventResults.events.map((event, index) => (
              <Tr height={'40px !important'} key={index} className='row'>
                <Td onClick={(): void => goToEvent(event.id)}>{event.id}</Td>
                <Td onClick={(): void => goToEvent(event.id)}>{event.name.substring(0, 45).concat(event.name.length > 45 ? '...' : '')}</Td>
                <Td onClick={(): void => goToEvent(event.id)}>{formatISODate(event.date)}</Td>
                <Td onClick={(): void => goToEvent(event.id)}>
                  <Text variant={event.status}>{t(getEventStatusLabelKey(event.status, event.successfulCheckInsCount))}</Text>
                </Td>
                <Td onClick={(): void => goToEvent(event.id)}>{event.participantCount}</Td>
                <Td>
                  <Link to={`/events/${event.id}`}>
                    <Button>
                      <i data-eva={'edit-2'} data-eva-fill={'#353F48'} data-eva-height='18' data-eva-width='16'></i>
                    </Button>
                  </Link>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={7} textAlign='center'>
                {t('table_no_results')}
              </Td>
            </Tr>
          )}
        </Tbody>
        <Tfoot>
          <Tr>
            <Td colSpan={7} padding={'0px'}>
              <Flex direction='row' justifyContent='space-between' alignItems='center' bg={'greyWarm100'}>
                <Flex direction='row' marginLeft='auto' alignItems='center'>
                  <Button onClick={(): void => handlePageChange(searchEvent.currentPage - 1)} isDisabled={searchEvent.currentPage === 1}>
                    <i data-eva={'chevron-left-outline'} data-eva-fill={'#353F48'}></i>
                  </Button>
                  <Button onClick={(): void => handlePageChange(searchEvent.currentPage + 1)} isDisabled={searchEvent.currentPage === eventResults?.totalPages}>
                    <i data-eva={'chevron-right-outline'} data-eva-fill={'#353F48'}></i>
                  </Button>
                </Flex>
              </Flex>
            </Td>
          </Tr>
        </Tfoot>
      </Table>
      <RetryDialog isOpen={isRetryDialogOpen} onClose={handleCloseRetryDialog} onConfirm={handleConfirmRetryDialog} message={errorData} />
    </div>
  )
}

export { SectionEventTable }
